import React from 'react';
import { Card, Col, Row, Spinner } from 'react-bootstrap';
import ProductTable from './ProductTable';
import ItemCard from './ItemCard';
import InfiniteScroll from 'react-infinite-scroller';
import { defaultImageUrl } from '../../config_and_helpers/config';
import { getProductImageUrl } from '../../utils/productDisplay';
import { getShopListKey } from '../../utils/shopCatalog';
import { INFINITE_SCROLL_THRESHOLD_PX } from '../../utils/listSearchRequest';

/**
 * Renders a component for infinite scrolling of products.
 *
 * @param {boolean} isSupplier - Indicates whether the user is a supplier.
 * @param {boolean} isShopView - Indicates whether the view is a shop view.
 * @param {boolean} isCatalogueView - Indicates whether the view is a catalogue view.
 * @param {boolean} similarProductsView - Indicates whether the similar products view is enabled.
 * @param {Array} products - The list of products to be rendered.
 * @param {function} resetSearchValue - The function to reset the search value.
 * @param {function} fetchProducts - The function to fetch more products.
 * @param {boolean} hasMore - Indicates whether there are more products to be fetched.
 * @param {Array} similarProducts - The list of similar products.
 * @param {function} setSimilarProducts - The function to set the list of similar products.
 * @param {boolean} isLoading - Indicates whether the products are currently being loaded.
 * @param {boolean} isSearching - Indicates whether a search is currently in progress.
 * @param {boolean} [useGlassTable=false] - Wrap inventory table in glass-table-wrap.
 * @param {string} [searchInput=''] - Current search text for empty-state messaging.
 * @returns {JSX.Element} The rendered component for infinite scrolling of products.
 */
const ProductInfiniteScrollComponent = ({
  isSupplier,
  isShopView,
  isCatalogueView,
  similarProductsView,
  products,
  resetSearchValue,
  fetchProducts,
  onLoadMore,
  hasMore,
  similarProducts,
  setSimilarProducts,
  isLoading,
  isSearching,
  sortBy,
  sortDirection,
  onSort,
  useGlassTable = false,
  searchInput = '',
}) => {
  /**
   * Renders a list of products or a loading spinner.
   *
   * @returns {JSX.Element} The rendered component.
   */
  const listToRender = () => {
    if (!products.length && isLoading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
          {isSearching && (
            <div className="mt-2 text-muted">Searching products...</div>
          )}
        </div>
      );
    }

    if (!products.length) {
      if (useGlassTable) {
        return (
          <div className="glass-empty">
            <div className="mb-3" style={{ fontSize: '2.5rem' }} aria-hidden="true">
              📦
            </div>
            <h5 className="mb-2">No products found</h5>
            <p className="mb-0">
              {searchInput.length >= 2
                ? `No products match your search for "${searchInput}"`
                : 'Nothing to display yet.'}
            </p>
          </div>
        );
      }
      return <span>Nothing to display...</span>;
    }

    if (!isCatalogueView && !isShopView) {
      const table = (
        <ProductTable
          isSupplier={isSupplier}
          productList={products}
          handleEdit={() => fetchProducts(true)}
          clearSearchValue={resetSearchValue}
          similarProductsView={similarProductsView}
          similarProducts={similarProducts}
          setSimilarProducts={setSimilarProducts}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={onSort}
        />
      );

      return useGlassTable ? (
        <div className="glass-table-wrap">{table}</div>
      ) : (
        table
      );
    }

    if (similarProductsView) {
      return (
        <Row>
          {products.map(product => (
            <Col key={product.id} sm={12} md={6} lg={4} className="mb-4">
              <Card>
                <a
                  href={getProductImageUrl(product, defaultImageUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Card.Img
                    variant="top"
                    src={getProductImageUrl(product, defaultImageUrl)}
                    alt={product.name}
                  />
                </a>
                <Card.Body>
                  <Card.Title>{product.name}</Card.Title>
                  <Card.Text>{product.cat_num}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      );
    }

    return (
      <div className="bg-dark-subtle">
        <div className="d-flex flex-wrap flex-row justify-content-center">
          {products.map(product => {
            const rowKey = isShopView ? getShopListKey(product) : product.id;
            return (
            <div className="item-card" key={rowKey}>
              <ItemCard
                product={product}
                handleEdit={() => fetchProducts(true)}
                isCatalogueView={isCatalogueView}
              />{' '}
            </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <InfiniteScroll
      pageStart={0}
      loadMore={() => {
        if (isLoading || !hasMore) {
          return;
        }
        if (onLoadMore) {
          onLoadMore();
          return;
        }
        fetchProducts(false);
      }}
      hasMore={hasMore}
      loader={
        !products.length && isLoading ? null : (
          <div key="products-loader" className="text-center py-2">
            <Spinner animation="border" size="sm" role="status" />
            <div className="mt-1">
              <small className="text-muted">Loading more products...</small>
            </div>
          </div>
        )
      }
      useWindow={true}
      threshold={INFINITE_SCROLL_THRESHOLD_PX}
      getScrollParent={() => document.body}
    >
      <div key="product-content-wrapper">{listToRender()}</div>
    </InfiniteScroll>
  );
};
export default ProductInfiniteScrollComponent;
